import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { convertToNgbDate, replaceStringTemplate } from "@app/modules/common";
import { AppQuery } from "@app/modules/common/app.store";
import {
  AuthenticationService,
  ProductSettingService,
  WarehouseService,
  WebLayoutService,
} from "@app/modules/common/services";
import { BaseFormDirective, ScanResult, SortOrder, TableColumn, TableComponent } from "@modules/common/components";
import { LookupListService } from "@modules/common/services/lookup-list.service";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import {
  codeOnly,
  JobDto,
  JobHoldTypeDto,
  JobMachineDto,
  LookupListEx,
  LookupObjectDto,
  MachineMasterDto,
  MachineMasterWithStockDto,
  ModelMasterDto,
  ResourceMasterDto
} from "@shared/models";
import { FieldSettings } from "@shared/models/FieldSettings";
import { filter, map } from "rxjs/operators";

interface LoanStage {
  /**
   * The next stage for the Loan Unit
   */
  to: string;
  /**
   * Icon to show in the UI
   */
  icon: string;
  /**
   * Label to show in the UI
   */
  label: string;
}

interface MenuAction {
  action: string;
  useJob: boolean;
  location: string;
  binId?: string;
  fieldSettings?: FieldSettings;
  // warehouseEditable?: boolean;
  // binEditable?: boolean;
}

const defaultFieldSettings: FieldSettings = {
  serialNumber: {
    validates: false
  }
};

interface MenuItem {
  description: string;
  actions: MenuAction[];
}

@Component({
  selector: "abi-loan-unit",
  templateUrl: "./loan-unit.component.html",
  styleUrls: ["./loan-unit.component.scss"],
  providers: [
    // { provide: VALIDATION_MESSAGES, useValue: validationMessages},
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoanUnitComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LoanUnitComponent),
      multi: true,
    },
  ],
})
export class LoanUnitComponent
  implements OnInit, ControlValueAccessor, Validator, OnChanges
{
  @Input() isPublic: boolean;
  @Input() userCanEdit: boolean;
  @Input() resource: ResourceMasterDto;
  @Input() job: JobDto;
  @Input() formErrors: { [control: string]: Array<string> } = {};
  @Output() createDeliveryNote: EventEmitter<void> = new EventEmitter();

  maxDate: NgbDateStruct;
  group: FormGroup;
  holds: LookupListEx<JobHoldTypeDto>;
  private machines: JobMachineDto[];

  readonly minDate = convertToNgbDate(Date.today());
  showLoanUnits = false;// show load units selections (Product Setting)
  /**
   * Loan Machines are not within Warehouse stock, but we simulate their movement using the Location field
   * @todo: we can migrate this process to use Actual Warehouse stock (Whs/Bin movements)
   */
  externaLoanUnits = false;// I guess this has to do with externally sourced loan units (also Product Setting)
  loanUnitCount = 0;
  canMovRentalUnit = true;
  private busy = false;

  canPrint = true;
  canCollect = true;
  canDeliver = true;

  readonly normalMenuItems: MenuItem[] = [
    {
      description: "Geyser/Tank Exchange",
      actions: [
        {
          action: "Return",
          useJob: true,
          location: null,
          fieldSettings: {
            warehouseId: {
              readonly: true,
            },
            binId: {
              readonly: true,
              defaultValue: "DMR"
            },
            // TODO: implement flexible configuration for serial number
            serialNumber: {
              validates: true,
            }
          }
        },
        {
          action: "Replace",
          useJob: false,
          location: null,
          fieldSettings: {
            warehouseId: {
              readonly: true,
            },
            binId: {
              readonly: true,
              defaultValue: "REPL"
            },
            serialNumber: {
              validates: true,
            }
          }
        },
      ],
    },
    {
      description: "Component Exchange",
      actions: [
        {
          action: "Return",
          useJob: false,
          location: null,
          fieldSettings: {
            warehouseId: {
              readonly: true,
            },
            binId: {
              readonly: true,
              defaultValue: "DMR"
            },
          }
        },
        {
          action: "Replace",
          useJob: false,
          location: null,
          fieldSettings: {
            warehouseId: {
              readonly: true,
            },
            binId: {
              readonly: true,
              defaultValue: "REPL"
            },
          }
        },
      ],
    },
    /*
    {
      description: "Return",
      actions: [
        {
          action: "Return",
          useJob: true,
          location: "Whs"
        }
      ]
    },*/
    {
      description: "Exchange",
      actions: [
        {
          action: "Return",
          useJob: true,
          location: null,
        },
        {
          action: "Replace",
          useJob: false,
          location: null,
        },
      ],
    },
    {
      description: "Credit",
      actions: [
        {
          action: "Return",
          useJob: true,
          location: null,
        },
      ],
    },
    {
      description: "Loan",
      actions: [
        {
          action: "Lend",
          useJob: false,
          location: "CsXF",
        },
      ],
    },
    {
      description: "Send Rental Unit for inspection",
      actions: [
        {
          action: "Uplift",
          useJob: true,
          location: "WhXF",
        },
      ],
    },

    /*
    {
      description: "Custom",
      actions: [
        {
          action: "",
          useJob: false,
          location: ""
        }
      ]
    }*/
  ];

  menuItems = this.normalMenuItems;

  loanStages: { [from: string]: LoanStage } = {
    Wh: {
      to: "CsXF",
      icon: "fa fa-arrow-alt-circle-right",
      label: "To Customer",
    },
    CsXF: {
      to: "Cs",
      icon: "fa fa-home",
      label: "At Customer",
    },
    Cs: {
      to: "WhXF",
      icon: "fa fa-arrow-alt-circle-left",
      label: "To Warehouse",
    },
    WhXF: {
      to: "Wh",
      icon: "fa fa-industry",
      label: "At Warehouse",
    },
  };
  warehouses: string[] = [];

  get machinesGroup(): FormArray {
    return this.group.get("machines") as FormArray;
  }

  get model(): ModelMasterDto {
    return this.job.machine.model;
  }

  private onTouched = () => {};

  moveActionsVisible = true;
  moveActionRoles = ["CC", "MAN", "SUPER", "ADMIN"];
  resourceMayAllowNegativeBalance = false;
  intangibleTransactions = false;
  allowLineReOpen = false;
  baseForm: BaseFormDirective;
  constructor(
    private productSettings: ProductSettingService,
    private lookups: LookupListService,
    private formBuilder: FormBuilder,
    private appQuery: AppQuery,
    private authService: AuthenticationService,
    private warehouseService: WarehouseService,
    private layoutService: WebLayoutService
  ) {
    this.baseForm = new BaseFormDirective(layoutService, formBuilder);
    this.moveActionRoles = this.productSettings.arrayValue("JobLoanUnitMoveActionsRoles", this.moveActionRoles);
    this.moveActionsVisible = this.appQuery.hasRoles(this.moveActionRoles);
    this.allowedLoanStages = this.productSettings.arrayValue("JobLoanUnitMoveStages", this.allowedLoanStages);
    this.resourceMayAllowNegativeBalance = this.productSettings.stringValue("JobLoanUnitAllowIntangible") === "resource";
    this.allowLineReOpen = this.productSettings.booleanValue("JobLoanUnitAllowReOpen");
    this.configureResource(this.resource);
    this.externaLoanUnits =
      !!this.productSettings.stringValue("JobShowLoanUnits");

    /**
     * Which Templates are allowed for this Job (Customer Specific)
     */
    const templates = this.productSettings
      .arrayValue("JobLoanTemplates", []);
    this.menuItems = this.normalMenuItems.filter((i) =>
      templates.includes(i.description)
    );
    this.group = formBuilder.group({
      machines: formBuilder.array([]),
      dummy: "abc",
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // get warehouses from Job resource - unless it's allowing external warehouses
    if (changes.resource?.currentValue && !this.externaLoanUnits) {
      this.warehouses = this.resource.warehouses;
    }
    if(changes.resource) {
      this.configureResource(this.resource);
    }
  }

  configureResource(resource: ResourceMasterDto) {
    if(resource && this.resourceMayAllowNegativeBalance) {
      // For specific use-caes we want to allow 'transactions' with no availability (KWIKOT)
      this.intangibleTransactions = this.resource.export || this.resource.external;
    } else {
      this.intangibleTransactions = false;
    }
  }

  get canAddLine(): boolean {
    return this.authService.hasRoles(this.addLineRoles);
  }

  get canCloseLine(): boolean {
    return this.authService.hasRoles(this.closeLineRoles);
  }

  addLineOptions = ["SINGLE", "TEMPLATE"];
  addLineRoles = ["SUPER"];
  closeLineRoles = ["COLL"];
  setupRoles() {
    this.addLineOptions = this.productSettings.arrayValue("JobLoansAddOptions", this.addLineOptions);
    this.addLineRoles = this.productSettings.arrayValue("JobLoansAddRoles", this.addLineRoles);
    this.closeLineRoles = this.productSettings.arrayValue("JobLoansCloseRoles", this.closeLineRoles);
  }

  private updateHolds() {
    this.canCollect = true;
    this.canPrint = true;
    this.canDeliver = true;
    this.job.holds
      .filter((h) => !h.status)
      .forEach((h) => {
        const hold = this.holds.item(h.typeId);
        if(!hold) return;
        if (
          hold.permissions.hasOwnProperty("PrintCollection") &&
          !hold.permissions["PrintCollection"]
        )
          this.canPrint = false;
        if (
          hold.permissions.hasOwnProperty("CompleteCollection") &&
          !hold.permissions["CompleteCollection"]
        )
          this.canCollect = false;
        if (
          hold.permissions.hasOwnProperty("CompleteDelivery") &&
          !hold.permissions["CompleteDelivery"]
        )
          this.canDeliver = false;
      });
    if (this.machines) this.setFormData(this.machines);
  }

  writeValue(machines: JobMachineDto[]): void {
    this.machines = machines;
    if (this.holds) this.updateHolds();
    // else
    //  this.setFormData(machines);
  }

  registerOnChange(fn: any): void {
    this.group.valueChanges
      .pipe(
        filter(() => !this.busy),
        map((v) => {
          this.getFormData(this.machines);
          return this.machines;
        })
      )
      .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled)
      this.group.disable({ emitEvent: false });
    else {
      this.group.enable();
      this.machinesGroup.controls.forEach(mac => {
        if (mac.value.printed && mac.value.closed)
          mac.disable();
      });
    }
  }

  validate(control: AbstractControl): ValidationErrors {
    return this.group.valid
      ? null
      : { invalidForm: { valid: false, message: "Invalid" } };
  }

  ngOnInit(): void {
    this.setupRoles();
    // Get HOLDS data from Lookups - this is a custom lookup type
    this.lookups.lookupList("CodeHoldType").subscribe((l) => {
      this.holds = l as LookupListEx<JobHoldTypeDto>;
      this.updateHolds();
    });
    this.showLoanUnits = this.productSettings.booleanValue("JobShowLoanUnits");
    this.maxDate = convertToNgbDate(Date.today().add(1, "month"));

    // use full warehouses list if the 'stringValue of 'JobShowLoanUnits' is 'truthy'
    if (this.externaLoanUnits)
      this.appQuery.$warehouses.subscribe((w) => (this.warehouses = w));
  }

  setFormData(model: JobMachineDto[]) {
    if (!model) return;
    this.showLoanUnits =
      this.showLoanUnits &&
      (!this.externaLoanUnits || this.job.contract?.sourceId === "TRS");
    // this.busy = true;
    this.machinesGroup.clear();
    const machinesGroup = model.map((l) => this.createLoanGroup(l));
    for (const grp of machinesGroup) this.machinesGroup.push(grp);
    // this.busy = false;
    // this.group.enable({onlySelf: true});

    // const rental = model.filter((l) => l.machine?.owner !== "Supplier");
    this.canMovRentalUnit = model
      .filter((m) => m.itemId === this.job.machine.machineId)
      .every((m) => m.closed);
    this.group.markAsPristine();
  }

  getFormData(machines: JobMachineDto[]): void {
    const loans = this.machinesGroup.getRawValue() as any[];
    let i = machines.length;
    while (i--) {
      const jobMachine: JobMachineDto = machines[i];
      const loan = loans.find((l) => l.sequence === jobMachine.sequence);

      if (!loan) machines.splice(i, 1);
    }
    for (const loan of loans) {
      let jobMachine = machines.find(
        (l) => l.sequence === loan.sequence && !!loan.sequence // !== "NEW"
      );
      if (!jobMachine) {
        jobMachine = {
          machine: null,
          requested: this.job.nextAction,
          sequence: "",
          action: "",
          deliveryNote: "",
          closed: 0,
          ordered: 1,
        };
        machines.push(jobMachine);
      }
      jobMachine.itemId =
        loan.machine && typeof loan.machine === "object"
          ? loan.machine.machineId || loan.machine.modelId
          : loan.machine;
      jobMachine.warehouseId = codeOnly(loan.warehouseId);
      jobMachine.binId = codeOnly(loan.binId);
      jobMachine.ordered = loan.ordered;
      jobMachine.fields = [loan.field1, loan.field2, loan.field3, loan.field4, loan.field5];
      // jobMachine.warrantyCode = loan.warrantyCode;/ TODO: implement this
      // if (loan.machine.machineId)
      jobMachine.machine = !!loan.machine?.machineId || (loan.closed  && !!loan.serialNumber) ? loan.machine : null;

      if (
        jobMachine.machine === this.job.machine ||
        (jobMachine.machine as any) === this.job.machine.machineId ||
        jobMachine.machine?.machineId === this.job.machine.machineId
      )
      jobMachine.machine = null;
      jobMachine.requested = loan.requested;
      jobMachine.delivered = loan.delivered;
      if (!loan.delivered && loan.closed) jobMachine.delivered = new Date();
      jobMachine.sequence = loan.sequence; // +loan.sequence > 0 ? loan.sequence : "";
      jobMachine.action = codeOnly(loan.action);
      jobMachine.deliveryNote = loan.deliveryNote;
      jobMachine.closed = loan.closed ? 1 : 0;
      jobMachine.serialNumber = loan.serialNumber;

      // jobId: this.job.jobId
      if (jobMachine.machine) {
        jobMachine.machine.locationId = codeOnly(loan.locationId);
        jobMachine.machine.warehouseId = codeOnly(loan.warehouseId);
        jobMachine.machine.serialNumber = loan.serialNumber;
      } else if (
        jobMachine.action === "Uplift" ||
        jobMachine.action === "Return"
      ) {
        this.job.machine.locationId = codeOnly(loan.locationId);
        this.job.machine.warehouseId = codeOnly(loan.warehouseId);
        if (loan.serialNumber)
          this.job.machine.serialNumber = loan.serialNumber;
      }
    }
  }

  public codeOnly(value: any): string {
    return codeOnly(value);
  }

  /**
   * DEV NOTE: Currently working on a Branch for 'feature/layout-admin-2' which ties into how the Form States are managed
   * We will need to Revisit this method to ensure that the Form State is managed correctly after the aforementioned branch is merged
   * @param line
   * @param canEdit
   * @param showMachines
   * @param locationId
   * @param warehouse
   * @param binId
   * @returns
   */
  private createLoanGroup(
    line: JobMachineDto,
    canEdit = false,
    showMachines = false,
    locationId = "",
    warehouse = "",
    binId = ""
  ) {
    if (!line.machine && line.itemId === this.job.machine.machineId) {
      line.machine = this.job.machine;
      line.fields = [this.job.machine.fields[0], this.job.machine.fields[1], this.job.machine.fields[2], this.job.machine.fields[3], this.job.machine.fields[4]];
      console.log('machine from job', line.machine)
    }
    locationId = locationId || line.machine?.locationId || "";
    const serialNumber = line.machine?.serialNumber || "";
    const item = this.formBuilder.group({
      machine: [null, Validators.required],
      ordered: 1,
      requested: this.job.nextAction,
      locationId: [{ value: locationId, disabled: true }, Validators.required],
      warehouseId: ["", Validators.required],
      binId: "",// TODO: might need to add validation
      serialNumber: "", // ["", RxwebValidators.unique()],
      // warrantyCode: "",// TODO: API to include warranty code
      field1: "",
      field2: "",
      field3: "",
      field4: "",
      field5: "",
      delivered: null,
      by: "",
      action: ["", Validators.required],
      sequence: "",
      deliveryNote: "",
      closed: 0,
      haveStock: false,
      printed: false,
      allowNegativeBalance: false,// NOTE: this must be updated from the Bin/Warehouse selection
    });
    if (line) {
      const machine = line.machine;
      const haveStock =
        !!+machine?.fields[4] ||
        (!!machine?.machineId && machine?.machineId !== "NEW");
      const warehouseId =
        warehouse || line.warehouseId || line.machine?.warehouseId || "";
      const formBinId = binId || line.binId || "";
      const formValues = {
        ...line,
        locationId,
        serialNumber,
        warehouseId,
        binId: formBinId,
        haveStock,
        field1: line.fields?.[0] || "",
        field2: line.fields?.[1] || "",
        field3: line.fields?.[2] || "",
        field4: line.fields?.[3] || "",
        field5: line.fields?.[4] || ""
      };
      console.log(line.machine, formValues);
      item.reset(formValues);

      if (machine && !line.machine && line.itemId) line.machine = machine;
      /*if (canEdit && !line.delivered) { // line.locationId !== "Wh" &&
        item.get("requested").enable();
      } else {
        item.get("requested").disable();
      }*/
      if (!line.machine) {
        item.get("closed").disable();
      }
      if (line.action) {
        item.get("action").disable();
      }
      if (canEdit && showMachines) {
        item.get("machine").enable();
      } else {
        item.get("machine").disable();
      }
      if (this.externaLoanUnits) item.get("locationId").disable();
      else item.get("requested").disable();
      if (+line.sequence > 0) {
        console.log('line is not new', line);
        item.get("action").disable();
        item.get("warehouseId").disable();
        item.get("binId").disable();
        if (this.externaLoanUnits || (!machine.machineId && (!haveStock && line.action !== "Return")))
          item.get("closed").disable();
        if (
          (line.action === "Replace" && !this.canDeliver) ||
          (line.action === "Return" && !this.canCollect)
        )
          item.get("closed").disable();
      }
      // line.closed ? item.disable({onlySelf: true}) : item.enable({onlySelf: true});


      if ((line.closed && (this.allowLineReOpen ? line.printed : true)) || !this.userCanEdit) {
        item.disable();
      }

      item.get("binId").valueChanges.subscribe(() => {
        this.updateLine(item);
      });
      item.get("warehouseId").valueChanges.subscribe(() => {
        item.get("binId").reset();
        this.updateLine(item);
      });

      this.updateLineBalanceSwitches(haveStock, item);
    }

    this.updateValidation(item);

    // apply negative value from whs/bin
    return item;
  }

  updateLine(line: FormGroup) {
    const lineValues = line.getRawValue();
    if(lineValues.warehouseId && lineValues.binId) {
      return this.warehouseService.getWarehouseBins(lineValues.warehouseId).toPromise().then(bins => {
        const bin = bins.find(b => b.code === lineValues.binId);
        // TODO: Update DTO with the Bin's allowNegativeBalance value (and add field to the Dialog Screen with ROLE locks)
        if(bin && this.intangibleTransactions) {
          line.get('allowNegativeBalance').setValue(true, {emitEvent: false});
        } else {
          line.get('allowNegativeBalance').setValue(false, {emitEvent: false});
        }
        return true;
      });
    } else {
      if(this.intangibleTransactions) {
        line.get('allowNegativeBalance').setValue(true, {emitEvent: false});
      } else {
        line.get('allowNegativeBalance').setValue(false, {emitEvent: false});
      }
      return Promise.resolve();
    }
  }

  updateValidation(line: FormGroup) {
    BaseFormDirective.getFieldSettings(this.layoutService, 'LoanUnit').subscribe(settings => {
      line.get('serialNumber').setValidators(this.getValidation('serialNumber', settings));
      line.get('serialNumber').updateValueAndValidity({emitEvent: false});
    });
  }

  // very basic validation fetcher
  getValidation(field: string, fieldSettings: FieldSettings): ValidatorFn[] {
    return fieldSettings[field]?.validates ? [Validators.required] : defaultFieldSettings[field]?.validates ? [Validators.required] : [];
  }

  /**
   * Configureable way to check for a condition based on input data
   * @param haveStock
   * @returns
   */
  stockCheck(haveStock, line: FormGroup) {
    if(line.get("allowNegativeBalance").value) {
      return true;
    }else {
      return haveStock;
    }
  }

  undoMovement(loan: FormGroup, id: number) {
    if (!this.externaLoanUnits || +loan.value.sequence <= 0) {
      if(!this.addLineOptions.includes("SINGLE")) {
        // Special Case: (eg: KWIKOT)
        // DEV NOTE: Ideally we must Manage the Lines by 'GROUP' as the lines are created "by Group" (template)
        for(let index = this.machinesGroup.length-1; index >= 0 ; index--) {
          // Don't Just Remove lines that cannot be removed
          if(this.canDelete(this.machineGroupAt(index))){
            this.machinesGroup.removeAt(index);
          }
        }
      } else {
        // Most Common Process: (KIC)
        this.machinesGroup.removeAt(id);
      }
    }
    else {
      let locationId = loan.getRawValue().locationId;
      const jobmachine = this.job.machines.find(
        (m) => m.sequence === loan.getRawValue().sequence
      );

      for (const [key, value] of Object.entries(this.loanStages)) {
        if (value.to === locationId) locationId = key;
      }

      const warehouseId = jobmachine.machine.warehouseId;
      loan.reset({ ...jobmachine, locationId, warehouseId });
      loan.get("closed").disable();
    }
    this.group.markAsDirty();
  }

  nextStage(loan: FormGroup) {
    const locationId = this.loanStages[loan.getRawValue().locationId].to;
    loan.patchValue({
      requested: new Date(),
      delivered: null,
      locationId,
      by: "",
    });
    loan.getRawValue().machine.locationId = locationId;
    loan.markAsDirty();
    loan.get("closed").enable();
    // this.form.markAsDirty();
  }

  newAction(item: MenuItem) {
    for (const act of item.actions) {
      const warehouse = this.appQuery.warehouse || this.resource?.warehouse;
      const bin = warehouse && act.fieldSettings?.['binId']?.defaultValue || "";

      // TODO: control whether Warehouse/Bin values must be passed into the Action Line

      const formGroup = act.useJob ? this.customerMovement(act.action, act.location, bin) : this.newMovement(act.action, act.location, warehouse, bin);
      if(act.fieldSettings?.['binId']?.readonly === true)
        formGroup.get('binId').disable({emitEvent: false});
      else
        formGroup.get('binId').enable({emitEvent: false});

      if(act.fieldSettings?.['warehouseId']?.readonly === true)
        formGroup.get('warehouseId').disable({emitEvent: false});
      else
        formGroup.get('warehouseId').enable({emitEvent: false});
    }
  }

  newMovement(action = "Lend", location = "CsXF", warehouse = "", bin = "") {
    const newUnit: JobMachineDto = {
      machine: null,
      requested: this.job.nextAction,
      // sequence: "",
      action,
      deliveryNote: "",
      closed: 0,
      sequence: (-500 - new Date().valueOf()).toString(),
      ordered: 1,
    };

    // this.job.machines.splice(0, 0, newUnit); // this.job.loans.indexOf(unit)
    const neww = this.createLoanGroup(newUnit, true, true, location, warehouse, bin);
    this.machinesGroup.push(neww);
    neww.markAsDirty();
    return neww;
  }

  unPrintedLines(): boolean {
    return this.machinesGroup.value.some((v) => !v.deliveryNote);
  }

  /**
   * Job related Movement
   * @param action
   * @param location
   * @returns
   */
  customerMovement(action = "Uplift", location = "WhXF", binId = "") {
    if (
      this.machinesGroup
      .getRawValue()
      .some((x) => x.machine === this.job.machine && !x.closed)
    )
      return;
    const newUnit: JobMachineDto = {
      machine: this.job.machine,
      fields: [this.job.machine.fields[0], this.job.machine.fields[1], this.job.machine.fields[2], this.job.machine.fields[3], this.job.machine.fields[4]],
      requested: this.job.nextAction,
      sequence: (-5000 - new Date().valueOf()).toString(),
      action,
      deliveryNote: "",
      closed: 0,
      ordered: 1,
    };
    if (location) this.job.machine.locationId = location;
    else location = this.job.machine.locationId;
    // this.job.machines.splice(0, 0, newUnit); // this.job.loans.indexOf(unit)
    const neww = this.createLoanGroup(
      newUnit,
      true,
      false,
      location,
      this.appQuery.warehouse2 ||
        this.resource?.warehouse2 ||
        this.warehouses[0],
      binId

    );
    // console.log(this.appQuery.warehouse2, this.resource?.warehouse2, this.warehouses[0])
    this.machinesGroup.push(neww);
    neww.markAsDirty();
    return neww;
  }

  allowedLoanStages = ["Wh", "CsXF", "Cs", "WhXF"];
  canMove(loan: FormGroup): boolean {
    const loanLine = loan.getRawValue();
    if(!this.moveActionsVisible) return false;
    // TODO: filter specific actions/locations
    if(!this.allowedLoanStages.includes(loanLine.locationId)) return false;
    const needDate = ["Wh", "Cs"];// TODO: integrate into field settings
    if (!this.externaLoanUnits || loan.dirty /*|| loan.disabled*/) return false;
    const mac = loan?.getRawValue().machine;
    const whId = loan?.getRawValue().warehouseId;
    const whOk = this.warehouses.includes(mac?.warehouseId || whId);
    const ok = whOk && !loan.getRawValue().closed; // !mac || check1 && !check2;
    return ok;
  }

  machineLabel(loan: FormGroup, template = '{modelId} ~ {name}'): string {
    const item = loan.getRawValue()?.machine;
    return item
      // ? `${item.modelId} ~ ${item.name}` // ~ ${item.serialNumber}
      ? replaceStringTemplate(template, item) // ~ ${item.serialNumber}
      : "~~";
  }

  toggleClosed(loan: FormGroup) {
    const loanData = loan.getRawValue();
    const closed = loanData.closed;

    if (closed) {
      // loan
        // .get("serialNumber")
      //  .setValidators([RxwebValidators.unique(), Validators.required]);
      // loan.get("locationId").enable();
      // if (!!loanData.machine?.machineId)
      // loan.get("warehouseId").enable();
      // else loan.get("warehouseId").disable();
    } else {
      // loan.get("serialNumber").setValidators(RxwebValidators.unique());
      // loan.get("locationId").disable();
      // loan.get("warehouseId").disable();
    }

    loan.get("serialNumber").updateValueAndValidity();
    // loan.markAsDirty();
  }

  newDeliveryNote() {
    // use a dirty hack to temporarily disable the
    this.canPrint = false;
    this.createDeliveryNote.emit();
  }

  actionSelected(action: LookupObjectDto, line: FormGroup) {
    // Uplift, Return"
    /*
    if (action.code === "Uplift" || action.code === "Return") {
      line.patchValue({
        machine: this.job.machine,
        locationId: this.job.machine.locationId,
        serialNumber: this.job.machine.serialNumber,
        warehouseId:
          this.appQuery.warehouse2 ||
          this.job.machine?.warehouseId ||
          line.value.warehouseId ||
          this.warehouses[0] ||
          "",
      });
      if (this.externaLoanUnits)
        line.get("machine").disable();
    } else {
      line.get("machine").enable();
      line.patchValue({
        warehouseId:
          line.value.warehouseId ||
          this.appQuery.warehouse ||
          this.warehouses[0] ||
          "",
      });
    }*/
  }

  machineSelected(machine: MachineMasterDto, line: FormGroup) {
    const avail = +machine.fields[4];
    const haveStock =
      !!avail || (!!machine.machineId && machine.machineId !== "NEW");

    line.patchValue({
      ...(line.get("warehouseId").disabled ? {} : { warehouseId: machine.warehouseId }),
      locationId: this.externaLoanUnits ? "CsXF" : machine.locationId,
      serialNumber: machine.machineId ? machine.serialNumber : "",
      haveStock,
    });
    this.updateLineBalanceSwitches(haveStock, line);
  }

  updateLineBalanceSwitches(haveStock, line: FormGroup) {
    this.updateLine(line).then(() => {
      const allowBalanceChange = this.stockCheck(haveStock, line);
      const lineValues = line.getRawValue();
      // Control if the Line can be "Closed"
      if (!lineValues.closed && (allowBalanceChange || codeOnly(lineValues.action) === "Return")) line.get("closed").enable({emitEvent: false});
      else line.get("closed").disable({emitEvent: false});
    });
  }

  warehouseSelected(warehouse: LookupObjectDto, line: FormGroup) {
    const machine: MachineMasterWithStockDto = line.getRawValue().machine;
    const result = warehouse.code + " ~ " + warehouse.description;
    if (machine != null) {
      const haveStock =
        machine.serialNumber || machine?.stockItems?.[warehouse.code];
      if (haveStock) line.get("closed").enable();
      else line.get("closed").disable();
    }
  }

  scanned(codes: ScanResult, loan: FormGroup) {
    loan.getRawValue().machine.serialNumber = codes.serialNumber;
  }

  userWarehouses(loan: FormGroup, warehouse: LookupObjectDto): boolean {
    if(loan.get('warehouseId').disabled) // don't filter when disabled
      return true;
    return (
      (!this.warehouses?.length && this.externaLoanUnits) ||
      this.warehouses.includes("*") ||
      this.warehouses.includes(warehouse.code)
    );
  }

  warehouseFormatter(line: FormGroup, warehouse: LookupObjectDto): string {
    const machine: MachineMasterWithStockDto = line.getRawValue().machine;
    let result = warehouse.code + " ~ " + warehouse.description;
    if (machine != null) {
      const wh = machine?.stockItems?.[warehouse.code];
      if (wh) {
        result = result + " [" + wh + "]";
      }
    }
    return result;
  }

  warehouseStock(line: FormGroup, warehouse: LookupObjectDto): string {
    const lValue = line.getRawValue();
    const machine: MachineMasterWithStockDto = lValue.machine;
    const action = codeOnly(lValue.action);
    if (action === "Lend" || action === "Replace") {
      if (machine != null) {
        const wh = machine?.stockItems?.[warehouse.code];
        if (wh) {
          return wh.toString();
        }
      }
      return "0";
    }
    return "";
  }

  canDelete(loan: FormGroup): boolean {
    return (this.externaLoanUnits && loan.dirty) ||
    (!this.externaLoanUnits &&
        (!loan.getRawValue()['closed'] ||
            loan.get('closed').enabled))
        ? true
        : false;
  }

  columns = [
    ...this.loanUnitColumns
  ];

  get loanUnitColumns(): TableColumn[] {
    return [
      {
        field: "action",
        header: "Exchange.Action",
        thClass: "w-80px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
        sortOrder: SortOrder.None,
      },
      {
        field: "machine",
        header: "Exchange.Model",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "ordered",
        header: "Exchange.Qty",
        thClass: "w-50px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
        mobile: true
      },
      {
        field: "warehouseId",
        header: "Exchange.Warehouse",
        thClass: "w-80px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
        mobile: true
      },
      {
        field: "binId",
        header: "Exchange.Bin",
        thClass: "w-50px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
        mobile: true
      },
      {
        field: "serialNumber",
        header: "Exchange.Serial",
        thClass: "w-150px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "field1",
        header: "Exchange.Field1",
        thClass: "w-100px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "field2",
        header: "Exchange.Field2",
        thClass: "w-100px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "field3",
        header: "Exchange.Field3",
        thClass: "w-100px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "field4",
        header: "Exchange.Field4",
        thClass: "w-100px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "field5",
        header: "Exchange.Field5",
        thClass: "w-100px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
      },
      {
        field: "locationId",
        header: "Exchange.Location",
        thClass: "w-50px",
        tdClass: "align-middle",
        defaultSort: SortOrder.None,
        mobile: true
      },
      {
        field: "requested",
        header: "Exchange.Request",
        thClass: "w-150px",
        defaultSort: SortOrder.None,
        mobile: true
      },
      {
        field: "deliveryNote",
        header: "Exchange.DeliveryNote",
        thClass: "w-80px",
        defaultSort: SortOrder.None,
      },
      {
        field: "actions",
        header: "",
        thClass: "w-80px",
        tdClass: "text-right align-middle",
        defaultSort: SortOrder.None,
        static: true,
      },
    ];
  }

  /**
   * This tracking function is instrumental in making this whole thing work...
   */
  loanUnitsTrackBy(index: number, item: JobMachineDto) {
    return item.itemId + item.machine?.modelId + item.sequence + item.action;
  }

  ordersSorted: (rows: JobMachineDto[], table: TableComponent) => void = (rows) => {
    console.log('orders sorted', rows);
  };

  machineGroupAt(i: number): FormGroup {
    return this.machinesGroup.at(i) as FormGroup;
  }
}
